import {
  Box,
  Button,
  Collapse,
  Flex,
  HStack,
  Icon,
  Link,
  Spacer,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { getAssetUrl } from '@cronos-app/chakra';
import {
  CollapsedText,
  Percentage,
  Price,
  Quantity,
} from '@cronos-app/components';
import { ChevronRight, Error, Warning } from '@cronos-app/icons';
import { NATIVE_TOKEN_ADDRESS } from '@cronos-app/sdk';
import { MaskIcon } from '@ui/components/MaskIcon';
import MobileSupportTooltip from '@ui/components/MobileSupportTooltip';
import {
  LINK_MAP,
  useCardSupportedTokens,
} from '@ui/views/card-cronos/core/configs';
import type { ExtendedTokenInfo } from '@ui/views/card-cronos/core/types';
import { useCardContextStore } from '@ui/views/card-cronos/core/useCardContextStore';
import {
  PriceImpactWarningType,
  useCdcDepositAddress,
  useEstimateTopUpGas,
  usePriceImpactWarningType,
  useSwapAndTopUp,
  useTokenUsdPriceByService,
  useValidAmountStrAndSwapAndTopUpInfo,
} from '@ui/views/card-cronos/core/utils';
import { useTranslations } from 'next-intl';
import { Fragment, useMemo, useState } from 'react';

import { DoubleCheck, useDoubleCheck } from '../DoubleCheck';

export const TopUpInfo = () => {
  const t = useTranslations();
  const { isConfirmed } = useDoubleCheck();
  const [isDoubleCheckOpen, setIsDoubleCheckOpen] = useState(false);
  const depositAddress = useCdcDepositAddress();
  const { amount, token, inputError } = useCardContextStore();
  const { validAmountStr, swapAndTopUpInfo } =
    useValidAmountStrAndSwapAndTopUpInfo();
  const { swapAndTopUp, loading } = useSwapAndTopUp(
    token,
    validAmountStr ?? '0',
  );
  const priceImpactWarningType = usePriceImpactWarningType();
  const isValid =
    !inputError &&
    !!validAmountStr &&
    swapAndTopUpInfo &&
    priceImpactWarningType !== PriceImpactWarningType.Error;
  const gas = useEstimateTopUpGas(token, amount);
  const allTokens = useCardSupportedTokens();
  const nativeTokenInfo = useMemo(
    () => allTokens.find((t) => t.address === NATIVE_TOKEN_ADDRESS),
    [allTokens],
  );
  return (
    <>
      <Button
        variant="ghost"
        bgColor="#159DFF"
        rounded="4px"
        onClick={isConfirmed ? swapAndTopUp : () => setIsDoubleCheckOpen(true)}
        isLoading={loading}
        isDisabled={!isValid}
        w="full"
        size="sm"
        textTransform="unset"
      >
        {swapAndTopUpInfo?.needToSwapFirst
          ? priceImpactWarningType === PriceImpactWarningType.Error
            ? t('card.Price Impact too High')
            : t('card.Swap & Top Up')
          : t('card.Top Up')}
      </Button>
      {priceImpactWarningType === PriceImpactWarningType.Warning && (
        <Flex my="2" gap="1">
          <Icon
            as={Warning}
            boxSize="4"
            color="brand.warn"
            sx={{
              path: {
                fill: 'currentColor',
              },
            }}
          />
          <Text textStyle="caption" color="brand.warn" flex="1">
            {t(
              'card.The price impact is higher than usual, please review the details below before proceeding',
            )}
          </Text>
        </Flex>
      )}
      <Box textStyle="caption" mt="2" mb="6">
        {t.rich('card.top-up-limitation-tips', {
          link: (c) => (
            <Link
              href={LINK_MAP.topUpLimitationLearnMore()}
              isExternal
              color="#159DFF"
            >
              {c}
            </Link>
          ),
        })}
      </Box>
      <VStack alignItems="stretch">
        <HStack justifyContent="space-between">
          <Text textStyle="body2">{t('card.Top Up to')}</Text>
          <CollapsedText
            w={{
              base: '80px',
              desktop: '120px',
            }}
            remain={4}
            text={depositAddress.data ?? ''}
            textStyle="body3Bold"
          />
        </HStack>
        {swapAndTopUpInfo?.needToSwapFirst && (
          <SwapSection
            priceImpact={swapAndTopUpInfo.priceImpactExcludeLpFee.toString()}
            to={swapAndTopUpInfo.toToken}
            amount={swapAndTopUpInfo.toAmount}
            slippageTolerance={swapAndTopUpInfo.slippageTolerance}
            paths={swapAndTopUpInfo.paths}
          />
        )}
        {!!gas && (
          <HStack justifyContent="space-between">
            <Text textStyle="body2">{t('card.Gas fee')}</Text>
            <HStack gap="1">
              <Quantity textStyle="body3Bold" value={gas} />
              {nativeTokenInfo?.Icon && <nativeTokenInfo.Icon boxSize="10px" />}
            </HStack>
          </HStack>
        )}
      </VStack>
      <DoubleCheck
        isOpen={isDoubleCheckOpen}
        onClose={() => setIsDoubleCheckOpen(false)}
        callback={swapAndTopUp}
      />
    </>
  );
};

const SwapSection = ({
  priceImpact,
  to,
  amount,
  slippageTolerance,
  paths,
}: {
  priceImpact: string;
  to: ExtendedTokenInfo;
  amount: string;
  slippageTolerance: number;
  paths: {
    percentage: number;
    path: string[];
  }[];
}) => {
  const t = useTranslations();
  const _price = useTokenUsdPriceByService(to);
  const [collapsed, setCollapsed] = useState(true);
  const priceImpactWarningType = usePriceImpactWarningType();
  const price = useMemo(() => _price?.mul(0.99), [_price]);
  return (
    <>
      <Flex>
        <Box>
          <MobileSupportTooltip
            hasArrow
            label={
              <Text
                w="250px"
                textStyle="body2"
                color="brand.gray-scale-light-grey"
              >
                {t('card.topup-output-tooltip')}
              </Text>
            }
            placement="top"
          >
            <HStack gap="1">
              <Text textStyle="body2">
                {t('card.Approx {symbol} received on CDC wallet', {
                  symbol: to.symbol,
                })}
              </Text>
              <MaskIcon
                icon={`url(${getAssetUrl('/masks/info.svg')})`}
                bg="brand.white"
                boxSize="16px"
              />
            </HStack>
          </MobileSupportTooltip>
          <Collapse in={collapsed}>
            <Box textStyle="caption" color="#159DFF">
              {paths.map(({ path, percentage }, index) => (
                <HStack key={index} gap="0.5">
                  <Box>
                    {path.map((symbol, i) => (
                      <Fragment key={symbol}>
                        {symbol}
                        {i < path.length - 1 ? ' -> ' : ' '}
                      </Fragment>
                    ))}
                    (
                    <Percentage
                      as="span"
                      display="inline-flex"
                      value={percentage.toString()}
                      suffix=" "
                    />
                    ) via VVS
                  </Box>
                  <MobileSupportTooltip
                    label={
                      <Text
                        w="250px"
                        textStyle="body2"
                        color="brand.gray-scale-light-grey"
                      >
                        {t(
                          `card.We'll automatically calculate the best route based on your preferences to get you an optimal price on your trade`,
                        )}
                      </Text>
                    }
                  >
                    <MaskIcon
                      icon={`url(${getAssetUrl('/masks/info.svg')})`}
                      bg="#159DFF"
                      boxSize="16px"
                    />
                  </MobileSupportTooltip>
                </HStack>
              ))}
            </Box>
          </Collapse>
        </Box>
        <Spacer />
        <Flex
          cursor="pointer"
          onClick={() => setCollapsed((prev) => !prev)}
          gap="1"
        >
          <Stack gap="0" alignItems="flex-end">
            <HStack gap="1">
              <Quantity textStyle="body3Bold" value={amount} />
              {to.Icon && <to.Icon boxSize="10px" />}
            </HStack>
            <Collapse in={collapsed}>
              <Price
                textStyle="body3"
                value={amount}
                price={price?.toFixed(to.decimals)}
                prefix="(~$"
                suffix=")"
              />
            </Collapse>
          </Stack>
          <Icon
            as={ChevronRight}
            boxSize="6"
            transform={collapsed ? 'rotate(90deg)' : 'rotate(-90deg)'}
            transition="transform 0.3s"
          />
        </Flex>
      </Flex>
      <HStack justifyContent="space-between">
        <Text textStyle="body3" color="brand.gray-scale-light-grey">
          {t('card.Slippage tolerance')}
        </Text>
        <Percentage
          color="#159DFF"
          textStyle="body3Bold"
          value={slippageTolerance.toString()}
        />
      </HStack>
      <HStack justifyContent="space-between">
        <MobileSupportTooltip
          hasArrow
          label={
            <Text
              w="250px"
              textStyle="body2"
              color="brand.gray-scale-light-grey"
            >
              {t(
                'card.This shows the price change from your trade Larger trades in smaller liquidity pools tend to have bigger price impact',
              )}
            </Text>
          }
          placement="top"
        >
          <HStack gap="1">
            <Text textStyle="body3" color="brand.gray-scale-light-grey">
              {t('card.Price impact')}
            </Text>
            <MaskIcon
              icon={`url(${getAssetUrl('/masks/info.svg')})`}
              bg="brand.white"
              boxSize="16px"
            />
          </HStack>
        </MobileSupportTooltip>
        <VStack
          alignItems="flex-end"
          gap="0"
          color={
            priceImpactWarningType === PriceImpactWarningType.Error
              ? 'brand.error'
              : priceImpactWarningType === PriceImpactWarningType.Warning
              ? 'brand.warn'
              : 'brand.white'
          }
        >
          <Percentage textStyle="body3Bold" value={priceImpact} />
          {priceImpactWarningType !== PriceImpactWarningType.Acceptable && (
            <HStack gap="1">
              <Icon
                as={
                  priceImpactWarningType === PriceImpactWarningType.Error
                    ? Error
                    : Warning
                }
                boxSize="4"
                color={
                  priceImpactWarningType === PriceImpactWarningType.Error
                    ? 'brand.error'
                    : 'brand.warn'
                }
                sx={{
                  path: {
                    fill: 'currentColor',
                  },
                }}
              />
              <Text textStyle="caption">
                {t('card.Warning! High Price impact!')}
              </Text>
            </HStack>
          )}
        </VStack>
      </HStack>
    </>
  );
};
